import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useApp } from '../context/AppContext';

const { FiBook, FiTrendingUp, FiAlertCircle, FiSave, FiArchive, FiFilter, FiUsers } = FiIcons;

const PLOAnalysis = () => {
  const { state, dispatch, assessmentService, performanceService, refreshAttainmentTargets } = useApp();
  const [ploData, setPloData] = useState([]);
  const [unmappedPLOs, setUnmappedPLOs] = useState([]);
  const [ploTargets, setPloTargets] = useState({});
  const [cloTargets, setCloTargets] = useState({});
  const [suggestions, setSuggestions] = useState([
    'Incorporate more real-world engineering problems',
    'Enhance laboratory sessions with modern equipment',
    'Increase focus on societal impact of engineering solutions'
  ]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [comments, setComments] = useState('');
  const [isTargetsLoaded, setIsTargetsLoaded] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  // Add state for class group filtering
  const [studentGroups, setStudentGroups] = useState({});
  const [activeGroup, setActiveGroup] = useState('All Groups');
  const [filteredStudents, setFilteredStudents] = useState([]);

  useEffect(() => {
    if (state.activeCourse) {
      loadExistingData();
      loadAttainmentTargets();
      // Organize students by class groups when component loads
      organizeStudentGroups();
    }
    if (state.studentsData.length > 0) {
      // Set filtered students initially to all students
      setFilteredStudents(state.studentsData);
    }
  }, [state.activeCourse, state.studentsData]);

  // Regenerate PLO analysis when filtered students change
  useEffect(() => {
    if (state.activeCourse && state.ploStatements && Object.keys(state.ploStatements).length > 0 && 
        filteredStudents.length > 0 && state.assessmentPlan.length > 0 && 
        state.ploMapping && Object.keys(state.ploMapping).length > 0 && isTargetsLoaded) {
      generatePLOAnalysisData();
    }
  }, [filteredStudents, state.assessmentPlan, state.ploMapping, state.ploStatements, isTargetsLoaded]);

  // Load targets from global state when available
  useEffect(() => {
    console.log('🎯 PLO Analysis: Attainment targets changed in global state', state.attainmentTargets);
    
    if (state.attainmentTargets.ploTargets && Object.keys(state.attainmentTargets.ploTargets).length > 0) {
      console.log('🎯 PLO Analysis: Setting PLO targets from global state:', state.attainmentTargets.ploTargets);
      setPloTargets(state.attainmentTargets.ploTargets);
      
      if (state.attainmentTargets.cloTargets && Object.keys(state.attainmentTargets.cloTargets).length > 0) {
        console.log('🎯 PLO Analysis: Setting CLO targets from global state:', state.attainmentTargets.cloTargets);
        setCloTargets(state.attainmentTargets.cloTargets);
        setIsTargetsLoaded(true);
      }
    }
  }, [state.attainmentTargets]);

  const organizeStudentGroups = () => {
    const groups = { 'All Groups': [] };
    
    state.studentsData.forEach(student => {
      const group = student.classGroup || 'Ungrouped';
      const faculty = student.faculty || '';
      
      // Add to All Groups
      groups['All Groups'].push(student);
      
      // Initialize group if it doesn't exist
      if (!groups[group]) {
        groups[group] = [];
      }
      
      // Add student to their group
      groups[group].push(student);
    });
    
    setStudentGroups(groups);
  };

  const switchClassGroup = (groupName) => {
    setActiveGroup(groupName);
    
    if (groupName === 'All Groups') {
      setFilteredStudents(state.studentsData);
    } else if (studentGroups[groupName]) {
      setFilteredStudents(studentGroups[groupName]);
    }
  };

  const loadExistingData = async () => {
    if (!state.activeCourse) return;
    setLoading(true);
    try {
      // First try to load from the new table
      const { data, error } = await performanceService.getCommentsAndSuggestions(
        state.activeCourse.id,
        'plo_analysis'
      );
      if (data) {
        setComments(data.comments || '');
        setSuggestions(data.suggestions || suggestions);
        console.log('Loaded comments and suggestions from new table:', data);
      } else {
        // If not found in new table, try the old table for backward compatibility
        const oldData = await performanceService.getPerformanceAnalysis(
          state.activeCourse.id,
          'plo_analysis'
        );
        if (!oldData.error && oldData.data) {
          setComments(oldData.data.comments || '');
          if (oldData.data.data?.suggestions) {
            setSuggestions(oldData.data.data.suggestions);
          }
          console.log('Loaded comments from old table (backward compatibility)');
        }
      }
    } catch (error) {
      console.error('Error loading PLO analysis data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAttainmentTargets = async () => {
    if (!state.activeCourse) return;
    try {
      console.log('🎯 PLO Analysis: Loading attainment targets');
      
      // First try to get from global state
      if (state.attainmentTargets.ploTargets && Object.keys(state.attainmentTargets.ploTargets).length > 0) {
        console.log('🎯 PLO Analysis: Using targets from global state:', state.attainmentTargets.ploTargets);
        setPloTargets(state.attainmentTargets.ploTargets);
        
        if (state.attainmentTargets.cloTargets && Object.keys(state.attainmentTargets.cloTargets).length > 0) {
          console.log('🎯 PLO Analysis: Using CLO targets from global state:', state.attainmentTargets.cloTargets);
          setCloTargets(state.attainmentTargets.cloTargets);
          setIsTargetsLoaded(true);
          return;
        }
      }

      // If not in global state, refresh from database
      if (Object.keys(state.attainmentTargets.ploTargets || {}).length === 0 || 
          Object.keys(state.attainmentTargets.cloTargets || {}).length === 0) {
        console.log('🎯 PLO Analysis: Refreshing targets from database');
        await refreshAttainmentTargets(state.activeCourse.id);
        
        // Try again from global state after refresh
        if (state.attainmentTargets.ploTargets && Object.keys(state.attainmentTargets.ploTargets).length > 0) {
          console.log('🎯 PLO Analysis: Using refreshed PLO targets:', state.attainmentTargets.ploTargets);
          setPloTargets(state.attainmentTargets.ploTargets);
        } else {
          // Set default targets of 50% if none exist
          console.log('🎯 PLO Analysis: Setting default PLO targets');
          const defaultPloTargets = {};
          Object.keys(state.ploStatements || {}).forEach(plo => {
            defaultPloTargets[plo] = 50;
          });
          setPloTargets(defaultPloTargets);
        }
        
        if (state.attainmentTargets.cloTargets && Object.keys(state.attainmentTargets.cloTargets).length > 0) {
          console.log('🎯 PLO Analysis: Using refreshed CLO targets:', state.attainmentTargets.cloTargets);
          setCloTargets(state.attainmentTargets.cloTargets);
        } else {
          // Set default CLO targets
          console.log('🎯 PLO Analysis: Setting default CLO targets');
          const defaultCloTargets = {};
          Object.keys(state.cloStatements || {}).forEach(clo => {
            defaultCloTargets[clo] = 50;
          });
          setCloTargets(defaultCloTargets);
        }
      }
      
      setIsTargetsLoaded(true);
    } catch (error) {
      console.error('Error loading PLO attainment targets:', error);
    }
  };

  const generatePLOAnalysisData = () => {
    console.log('🎯 PLO Analysis: Generating PLO analysis with targets:', ploTargets);
    
    const ploAnalysisData = [];
    const unmappedPlosList = [];
    
    setDebugInfo({
      ploTargets,
      cloTargets,
      ploMapping: state.ploMapping,
      cloStatements: state.cloStatements,
      ploStatements: state.ploStatements
    });

    // Get all PLOs from PLO statements
    Object.entries(state.ploStatements).forEach(([ploCode, description]) => {
      // Check if this PLO is mapped to any CLO
      let isMapped = false;
      Object.entries(state.ploMapping).forEach(([cloCode, ploMappings]) => {
        if (ploMappings[ploCode] === true) {
          isMapped = true;
        }
      });

      if (isMapped) {
        // This PLO is mapped to at least one CLO
        let studentsAchievedPLO = 0;
        const totalStudents = filteredStudents.length;

        // Get the target percentage for this PLO (from global state or default to 50%)
        const targetPercentage = ploTargets[ploCode] || 50;
        console.log(`🎯 PLO Analysis: Using target ${targetPercentage}% for ${ploCode}`);

        // For each student, check if they achieved this PLO
        filteredStudents.forEach(student => {
          // Find all CLOs mapped to this PLO
          const mappedCLOs = [];
          Object.entries(state.ploMapping).forEach(([cloCode, ploMappings]) => {
            if (ploMappings[ploCode] === true) {
              mappedCLOs.push(cloCode);
            }
          });

          // Calculate student's achievement for each mapped CLO
          let cloAchievements = 0;
          let totalMappedCLOs = mappedCLOs.length;

          mappedCLOs.forEach(cloCode => {
            // Get all assessments mapped to this CLO
            const cloAssessments = state.assessmentPlan.filter(assessment => assessment.clo === cloCode);
            if (cloAssessments.length === 0) return;

            let studentCLOMarks = 0;
            let studentCLOMaxMarks = 0;

            // Calculate student's total marks for this CLO
            cloAssessments.forEach(assessment => {
              const studentMark = student.marks[assessment.id] || 0;
              const maxMark = assessment.marks;
              studentCLOMarks += studentMark;
              studentCLOMaxMarks += maxMark;
            });

            // Calculate percentage for this CLO
            const cloPercentage = studentCLOMaxMarks > 0 ? (studentCLOMarks / studentCLOMaxMarks) * 100 : 0;

            // Student achieves CLO if they get the target percentage or more
            const cloTargetPercentage = cloTargets[cloCode] || 50;
            if (cloPercentage >= cloTargetPercentage) {
              cloAchievements++;
            }
          });

          // Student achieves PLO if they achieve at least the target percentage of mapped CLOs
          const ploAchievementPercentage = totalMappedCLOs > 0 ? (cloAchievements / totalMappedCLOs) * 100 : 0;
          if (ploAchievementPercentage >= targetPercentage) {
            studentsAchievedPLO++;
          }
        });

        // Calculate achievement percentage
        const achievementPercentage = totalStudents > 0 ? Math.round((studentsAchievedPLO / totalStudents) * 100) : 0;

        ploAnalysisData.push({
          plo: ploCode,
          description: description,
          attainment: achievementPercentage,
          targetPercentage: targetPercentage,
          status: achievementPercentage >= targetPercentage ? 'Achieved' : 'Not Achieved',
          studentsAchieved: studentsAchievedPLO,
          totalStudents: totalStudents
        });
      } else {
        // This PLO is not mapped to any CLO
        unmappedPlosList.push({
          plo: ploCode,
          description: description,
          status: 'Not Applicable',
          reason: 'Not mapped to any CLO in this course'
        });
      }
    });

    setPloData(ploAnalysisData);
    setUnmappedPLOs(unmappedPlosList);
    console.log('🎯 PLO Analysis: Calculated PLO data:', ploAnalysisData);
    console.log('🎯 PLO Analysis: Unmapped PLOs:', unmappedPlosList);
  };

  const addSuggestion = () => {
    setSuggestions([...suggestions, '']);
  };

  const updateSuggestion = (index, value) => {
    const newSuggestions = [...suggestions];
    newSuggestions[index] = value;
    setSuggestions(newSuggestions);
  };

  const removeSuggestion = (index) => {
    setSuggestions(suggestions.filter((_, i) => i !== index));
  };

  const saveAnalysis = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    setSaveLoading(true);
    try {
      // Save to the new table
      const { error: newError } = await performanceService.saveCommentsAndSuggestions(
        state.activeCourse.id,
        'plo_analysis',
        comments,
        suggestions
      );

      if (newError) throw new Error(newError);

      // For backward compatibility and to maintain previous functionality,
      // also save to the old table
      const analysisData = {
        ploData,
        unmappedPLOs,
        suggestions,
        achievementSummary: {
          totalPLOs: ploData.length + unmappedPLOs.length,
          mappedPLOs: ploData.length,
          achievedPLOs: ploData.filter(plo => plo.status === 'Achieved').length,
          notAchievedPLOs: ploData.filter(plo => plo.status === 'Not Achieved').length,
          unmappedPLOs: unmappedPLOs.length,
          averageAttainment: ploData.length > 0 ? Math.round(ploData.reduce((sum, plo) => sum + plo.attainment, 0) / ploData.length) : 0,
          totalStudents: ploData.length > 0 ? ploData[0].totalStudents : 0
        }
      };

      const { error } = await performanceService.savePerformanceAnalysis(
        state.activeCourse.id,
        'plo_analysis',
        analysisData,
        comments,
        '', // removed preparedBy
        '' // removed verifiedBy
      );

      if (error) throw new Error(error);

      alert('PLO Achievement Analysis saved successfully!');
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error saving PLO Achievement Analysis: ' + error.message);
    } finally {
      setSaveLoading(false);
    }
  };

  if (!state.activeCourse) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <SafeIcon icon={FiBook} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Course Selected</h3>
          <p className="text-gray-600">Please select a course to view PLO Achievement Analysis.</p>
        </div>
      </motion.div>
    );
  }

  if (!state.ploStatements || Object.keys(state.ploStatements).length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center py-8">
            <SafeIcon icon={FiAlertCircle} className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No PLO Statements Found</h3>
            <p className="text-gray-600 mb-4">
              You need to set up Programme Learning Outcomes (PLOs) first before analyzing their achievement.
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (state.studentsData.length === 0 || state.assessmentPlan.length === 0 || !state.ploMapping || Object.keys(state.ploMapping).length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center py-8">
            <SafeIcon icon={FiAlertCircle} className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Insufficient Data for PLO Analysis</h3>
            <p className="text-gray-600 mb-4">
              To perform PLO Achievement Analysis, you need:
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
              <h4 className="font-semibold text-yellow-900 mb-2">Required Data:</h4>
              <ul className="text-yellow-800 space-y-1 list-disc list-inside">
                <li>PLO statements</li>
                <li>Assessment Plan with CLO mappings</li>
                <li>CLO-PLO mapping</li>
                <li>Student marks data</li>
              </ul>
              <div className="mt-4 space-y-2">
                <p className="text-sm">
                  <span className="font-medium">PLO Statements:</span>
                  <span className={Object.keys(state.ploStatements).length > 0 ? "text-green-600 ml-1" : "text-red-600 ml-1"}>
                    {Object.keys(state.ploStatements).length > 0 ? "✓ Available" : "✗ Missing"}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-medium">Assessment Plan:</span>
                  <span className={state.assessmentPlan.length > 0 ? "text-green-600 ml-1" : "text-red-600 ml-1"}>
                    {state.assessmentPlan.length > 0 ? "✓ Available" : "✗ Missing"}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-medium">CLO-PLO Mapping:</span>
                  <span className={Object.keys(state.ploMapping).length > 0 ? "text-green-600 ml-1" : "text-red-600 ml-1"}>
                    {Object.keys(state.ploMapping).length > 0 ? "✓ Available" : "✗ Missing"}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-medium">Student Data:</span>
                  <span className={state.studentsData.length > 0 ? "text-green-600 ml-1" : "text-red-600 ml-1"}>
                    {state.studentsData.length > 0 ? `✓ ${state.studentsData.length} students` : "✗ No students"}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Course Info Header */}
      <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
        <h2 className="text-xl font-bold text-orange-900 mb-2">
          {state.activeCourse.course_name || state.activeCourse.courseName} ({state.activeCourse.course_code || state.activeCourse.courseCode})
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="font-medium text-orange-700">Faculty:</span>
            <p className="text-orange-600">{state.activeCourse.faculty}</p>
          </div>
          <div>
            <span className="font-medium text-orange-700">Programme:</span>
            <p className="text-orange-600">{state.activeCourse.programme}</p>
          </div>
          <div>
            <span className="font-medium text-orange-700">Total PLOs:</span>
            <p className="text-orange-600">{ploData.length + unmappedPLOs.length}</p>
          </div>
          <div>
            <span className="font-medium text-orange-700">Mapped PLOs:</span>
            <p className="text-orange-600">{ploData.length} ({ploData.filter(plo => plo.status === 'Achieved').length} Achieved)</p>
          </div>
        </div>
      </div>

      {/* Class Group Selection Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center">
            <SafeIcon icon={FiFilter} className="w-5 h-5 text-orange-600 mr-2" />
            <h2 className="text-lg font-bold text-gray-900">Filter by Class Group</h2>
          </div>
          <div className="text-sm text-orange-600 font-medium">
            {Object.keys(studentGroups).length > 0 && (
              <span>{Object.keys(studentGroups).length - 1} group(s) available</span>
            )}
          </div>
        </div>
        
        {/* Class Group Tabs */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {Object.keys(studentGroups).map(groupName => (
              <button
                key={groupName}
                onClick={() => switchClassGroup(groupName)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeGroup === groupName 
                    ? 'bg-orange-600 text-white'
                    : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                }`}
              >
                <div className="flex items-center">
                  <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                  <span>{groupName}</span>
                  <span className="ml-2 bg-white text-orange-800 text-xs px-2 py-0.5 rounded-full">
                    {studentGroups[groupName].length}
                  </span>
                </div>
              </button>
            ))}
          </div>
          
          {/* Active Group Info */}
          {activeGroup && activeGroup !== 'All Groups' && (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mt-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-orange-900">
                    <SafeIcon icon={FiUsers} className="w-4 h-4 inline mr-2" />
                    {activeGroup} Class Group
                  </h3>
                  <p className="text-sm text-orange-700 mt-1">
                    Showing PLO analysis for {studentGroups[activeGroup]?.length || 0} students
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          PLO Achievement Analysis
          {activeGroup !== 'All Groups' && (
            <span className="ml-2 text-lg text-orange-600">({activeGroup})</span>
          )}
        </h2>
        
        {/* Target Settings Info */}
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="font-semibold text-green-900 mb-2">🎯 Current Target Settings</h4>
          <p className="text-green-800 text-sm mb-3">
            The analysis below uses the target attainment percentages set in the CLO-PLO Mapping page. 
            Students must achieve at least the target percentage in each PLO for it to be considered "Achieved".
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-40 overflow-y-auto">
            {Object.entries(ploTargets).map(([plo, target]) => (
              <div key={plo} className="bg-white p-2 rounded border border-green-200">
                <span className="font-medium text-green-800">{plo}:</span>
                <span className="ml-2 text-green-600">{target}%</span>
              </div>
            ))}
          </div>
          <p className="text-green-700 text-xs mt-2">
            To modify these targets, go to CLO-PLO Mapping → Target Settings
          </p>
        </div>

        {/* Historical Data Reference */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <SafeIcon icon={FiArchive} className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-blue-900">Historical Achievement Data</h4>
          </div>
          <p className="text-blue-800 text-sm">
            To compare current PLO achievement with previous semesters, please refer to the <strong>Archived Reports</strong> section. This provides comprehensive historical data and trends for better analysis and comparison.
          </p>
        </div>

        {/* PLO Mapping Status Summary */}
        <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <h3 className="font-semibold text-orange-900 mb-2">PLO Mapping Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded-lg border border-orange-200">
              <h4 className="font-medium text-orange-800 text-sm">Total PLOs</h4>
              <p className="text-2xl font-bold text-orange-600">{ploData.length + unmappedPLOs.length}</p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-orange-200">
              <h4 className="font-medium text-orange-800 text-sm">Mapped to CLOs</h4>
              <p className="text-2xl font-bold text-orange-600">{ploData.length}</p>
              <p className="text-xs text-orange-600">
                {Math.round((ploData.length / (ploData.length + unmappedPLOs.length)) * 100)}% of total PLOs
              </p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-orange-200">
              <h4 className="font-medium text-orange-800 text-sm">Unmapped PLOs</h4>
              <p className="text-2xl font-bold text-orange-600">{unmappedPLOs.length}</p>
              <p className="text-xs text-orange-600">
                {Math.round((unmappedPLOs.length / (ploData.length + unmappedPLOs.length)) * 100)}% of total PLOs
              </p>
            </div>
          </div>
        </div>

        {/* PLO Achievement Analysis Table - Only for Mapped PLOs */}
        {ploData.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">PLO Achievement Analysis (Mapped PLOs)</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">PLO</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">PLO Statement/Description</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Target Attainment (%)</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Students Achieved (Count)</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Percentage of Student (Attained Target)</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Status of PLO Achievement</th>
                  </tr>
                </thead>
                <tbody>
                  {ploData.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 px-4 py-2 font-medium">{item.plo}</td>
                      <td className="border border-gray-300 px-4 py-2">
                        <div className="max-w-md">
                          <p className="text-sm">{item.description}</p>
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                          {item.targetPercentage}%
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">{item.studentsAchieved}/{item.totalStudents}</td>
                      <td className="border border-gray-300 px-4 py-2">{item.attainment}%</td>
                      <td className={`border border-gray-300 px-4 py-2 font-medium ${item.status === 'Achieved' ? 'text-green-600' : 'text-red-600'}`}>
                        {item.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Unmapped PLOs Table */}
        {unmappedPLOs.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Unmapped PLOs</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">PLO</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">PLO Statement/Description</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Status</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {unmappedPLOs.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 px-4 py-2 font-medium">{item.plo}</td>
                      <td className="border border-gray-300 px-4 py-2">
                        <div className="max-w-md">
                          <p className="text-sm">{item.description}</p>
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-gray-500 font-medium">
                        {item.status}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-gray-500">
                        {item.reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PLO Achievement Summary - Only for Mapped PLOs */}
        {ploData.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">PLO Achievement Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-green-800">Achieved PLOs</h4>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {ploData.filter(plo => plo.status === 'Achieved').length}/{ploData.length}
                </p>
                <p className="text-sm text-green-600">
                  {ploData.length > 0 ? Math.round((ploData.filter(plo => plo.status === 'Achieved').length / ploData.length) * 100) : 0}% Success Rate
                </p>
              </div>
              <div className={`p-4 rounded-lg border ${ploData.filter(plo => plo.status === 'Not Achieved').length === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiAlertCircle} className={`w-5 h-5 ${ploData.filter(plo => plo.status === 'Not Achieved').length === 0 ? 'text-green-600' : 'text-red-600'}`} />
                  <h4 className={`font-semibold ${ploData.filter(plo => plo.status === 'Not Achieved').length === 0 ? 'text-green-800' : 'text-red-800'}`}>Not Achieved PLOs</h4>
                </div>
                <p className={`text-2xl font-bold ${ploData.filter(plo => plo.status === 'Not Achieved').length === 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {ploData.filter(plo => plo.status === 'Not Achieved').length}/{ploData.length}
                </p>
                <p className={`text-sm ${ploData.filter(plo => plo.status === 'Not Achieved').length === 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {ploData.filter(plo => plo.status === 'Not Achieved').length === 0 ? 'Excellent Performance! 🎉' : 'Need Improvement'}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800">Average Attainment</h4>
                <p className="text-2xl font-bold text-blue-600">
                  {ploData.length > 0 ? Math.round(ploData.reduce((sum, plo) => sum + plo.attainment, 0) / ploData.length) : 0}%
                </p>
                <p className="text-sm text-blue-600">Current Semester</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-800">Total Students</h4>
                <p className="text-2xl font-bold text-purple-600">{filteredStudents.length}</p>
                <p className="text-sm text-purple-600">Analyzed</p>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Comments */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Analysis Comments</h3>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            className="w-full h-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter your detailed analysis and observations on PLO achievement..."
          />
        </div>

        {/* Improvement Recommendations - Only for PLOs that need improvement */}
        {ploData.some(plo => plo.status === 'Not Achieved') && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="font-semibold text-red-900 mb-3">⚠️ PLOs Requiring Attention:</h3>
            <div className="space-y-2">
              {ploData.filter(plo => plo.status === 'Not Achieved').map((plo, index) => (
                <div key={index} className="bg-white p-3 rounded border border-red-200">
                  <div className="flex items-start space-x-3">
                    <span className="font-medium text-red-800 min-w-0">{plo.plo}:</span>
                    <div className="flex-1">
                      <p className="text-red-700 text-sm">{plo.description}</p>
                      <p className="text-red-600 text-xs mt-1">
                        Achievement: {plo.attainment}% ({plo.studentsAchieved}/{plo.totalStudents} students) | Target: {plo.targetPercentage}% | Shortfall: {Math.max(0, plo.targetPercentage - plo.attainment)}%
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Success Message - Show when all PLOs are achieved */}
        {ploData.length > 0 && ploData.every(plo => plo.status === 'Achieved') && (
          <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="font-semibold text-green-900 mb-3">🎉 Excellent Performance!</h3>
            <p className="text-green-800">
              Congratulations! All mapped PLOs have been achieved by at least the target percentage of students. This demonstrates excellent alignment between course delivery and programme learning outcomes.
            </p>
          </div>
        )}

        {/* PLO Mapping Recommendation - For Unmapped PLOs */}
        {unmappedPLOs.length > 0 && (
          <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="font-semibold text-yellow-900 mb-3">📝 PLO Mapping Recommendations:</h3>
            <p className="text-yellow-800 mb-3">
              {unmappedPLOs.length} PLOs are not mapped to any CLO in this course. Consider updating the CLO-PLO mapping if these PLOs should be addressed in this course, or leave them unmapped if they are not relevant.
            </p>
            <div className="space-y-2">
              {unmappedPLOs.map((plo, index) => (
                <div key={index} className="bg-white p-3 rounded border border-yellow-200">
                  <div className="flex items-start space-x-3">
                    <span className="font-medium text-yellow-800 min-w-0">{plo.plo}:</span>
                    <div className="flex-1">
                      <p className="text-yellow-700 text-sm">{plo.description}</p>
                      <p className="text-yellow-600 text-xs mt-1">
                        Map this PLO to relevant CLOs in the CLO-PLO Mapping section if it should be addressed in this course.
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggestions for Improvement */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Suggestions for Improvement of PLO Achievement</h3>
          <div className="space-y-3">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">{index + 1}.</span>
                <input
                  type="text"
                  value={suggestion}
                  onChange={(e) => updateSuggestion(index, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter suggestion for improvement"
                />
                <button
                  onClick={() => removeSuggestion(index)}
                  className="text-red-600 hover:text-red-800 px-2 py-1"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              onClick={addSuggestion}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              + Add Suggestion
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={saveAnalysis}
            disabled={saveLoading}
            className="flex items-center space-x-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
          >
            {saveLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <SafeIcon icon={FiSave} className="w-4 h-4" />
            )}
            <span>{saveLoading ? 'Saving...' : 'Save PLO Analysis'}</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default PLOAnalysis;